/**
 * The share button, what it says, and the dialog behind it.
 *
 * Three things here are decisions rather than plumbing.
 *
 * **The button says how many sessions are there.** Sharing needs another session open, and
 * that is not something anybody can check from the outside — a button that said "Share"
 * and then reported "nobody to share with" would be asking people to guess. So the count
 * is on the button, and with nobody there it is disabled and says so.
 *
 * **And a dot in its corner says whether they can be reached**, which the count does not:
 * sessions can be signed in, be counted here, and still have no network route between
 * them, which nothing on the page would show until somebody pressed the button and read
 * the apology. A corner of the button costs no room, moves nothing when it changes, and
 * needs no separate folded form. The words are in the button's tooltip and in a status
 * line that never appears on the screen, because a colour says nothing to somebody who
 * cannot see it — and cannot express "reachable, and one other asleep" to anybody at all.
 *
 * **Attaching an existing library to an account is asked about, not assumed.** Models
 * added before there were accounts belong to nobody. Sharing them means claiming them, and
 * on a shared computer that would silently hand somebody else's library to whoever signed
 * in. So the first share says how many and waits.
 *
 * **The dialog is markup, and is now only for the question.** Not `alert`, not `confirm`:
 * half of what this application is for runs in the Meta Quest browser, and anything that
 * asks the platform for a window is a bet on the platform having one — a bet already lost
 * once here, by the colour picker. Reporting what happened used the same dialog and no
 * longer does, because a modal window is a poor way to say something that has already
 * finished: it takes the whole page, has to be dismissed before anything else can be
 * done, and hides the sidebar the message is about. Those go to the notice board at the
 * foot of that sidebar instead. The question stays, because a note that can be ignored
 * cannot ask one.
 */

import { formatBytes } from '../format.js';
import type { Health } from '../domain/health.js';
import {
  NoOtherSessionsError,
  NoRouteError,
  type Progress,
  type ShareResult,
  type Sharing,
} from '../share/sharing.js';
import type { ModelStore } from '../storage/modelStore.js';
import { logger } from '../log.js';
import { buttonElement, dialogElement, element } from './dom.js';
import type { NoticeInput } from './noticeList.js';

const log = logger('share');

export interface SharePanelOptions {
  readonly sharing: Sharing;
  readonly store: ModelStore;
  /** True while the headset is presenting, when starting a share is not offered. */
  readonly isPresenting: () => boolean;
  /**
   * Report an outcome. Passed in rather than reached for, so this panel says what happened
   * and nothing about where it is shown.
   */
  readonly onNotice: (notice: NoticeInput) => void;
}

export class SharePanel {
  private readonly root = element('share');
  private readonly button = buttonElement('share-button');

  /**
   * The words on the button, which are a child of it rather than the whole of it.
   *
   * Folded away, the button keeps its icon and loses its label — so writing to the button
   * itself would replace the icon along with the text the first time the count changed.
   * What the label says still reaches somebody who cannot see the icon, through the title
   * this also sets, which is the button's accessible description either way.
   */
  private readonly label = element('share-label');

  /** The dot in the button's corner, and the same thing in words, off the screen. */
  private readonly lamp = element('share-lamp');
  private readonly announcement = element('share-health');

  /**
   * The progress line, which sits above the storage figure rather than under the button.
   *
   * Shown for exactly as long as a share is running. Nothing else needs to know: it is
   * empty between shares, and an empty block that is still in the layout is a gap that
   * opens and closes for no visible reason.
   */
  private readonly report = element('share-report');
  private readonly status = element('share-status');
  private readonly progress = progressElement('share-progress');

  private readonly dialog = dialogElement('share-dialog');
  private readonly dialogTitle = element('share-dialog-title');
  private readonly dialogMessage = element('share-dialog-message');
  private readonly dialogDetail = element('share-dialog-detail');
  private readonly dialogConfirm = buttonElement('share-dialog-confirm');
  private readonly dialogClose = buttonElement('share-dialog-close');

  private others = 0;
  private available = false;
  private busy = false;
  private health: Health | undefined;

  constructor(private readonly options: SharePanelOptions) {
    this.button.addEventListener('click', () => {
      void this.begin();
    });

    this.dialogClose.addEventListener('click', () => {
      this.dialog.close();
    });

    this.render();
  }

  /** Whether sharing is possible at all: signed in, with the connection up. */
  setAvailable(available: boolean): void {
    this.available = available;
    this.render();
  }

  setOtherSessions(others: number): void {
    this.others = others;
    this.render();
  }

  /**
   * Whether those sessions could actually be shared with.
   *
   * `undefined` before there is an answer, and again on signing out: the sharing
   * connection takes a moment to open, and a dot that starts on one colour and settles on
   * another invites everybody to read the first one.
   */
  setHealth(health: Health | undefined): void {
    this.health = health;
    this.render();
  }

  /** Entering VR takes the button away; leaving it brings it back. */
  handlePresentationChange(): void {
    this.render();
  }

  private render(): void {
    this.root.hidden = !this.available || this.options.isPresenting();

    // Ahead of the check below, because the state of the connection goes on changing while
    // a share runs and is worth showing when it does — it is only the words on the button
    // that a share takes over.
    this.drawLamp();
    if (this.busy) return;

    this.button.disabled = this.others === 0;
    this.label.textContent =
      this.others === 0
        ? 'No other sessions open'
        : this.others === 1
          ? 'Share with 1 other session'
          : `Share with ${this.others.toString()} other sessions`;

    const purpose =
      this.others === 0
        ? 'Open this site in another browser, or on your headset, and sign in there too.'
        : 'Copy models both ways, so every session has the same set.';

    // The dot's whole meaning, since the dot itself has room for a colour and no more.
    this.button.title =
      this.health === undefined
        ? purpose
        : `${purpose}\n\n${this.health.summary}. ${this.health.detail}`;
  }

  private drawLamp(): void {
    const health = this.health;
    this.lamp.hidden = health === undefined;
    this.announcement.textContent = health?.summary ?? '';
    if (health !== undefined) this.lamp.dataset.level = health.level;
  }

  private async begin(): Promise<void> {
    if (this.busy) return;

    const account = this.options.sharing.accountId;
    if (account === undefined) return;

    // Claiming a library for an account is not something to do behind somebody's back.
    const unowned = await this.options.store.countUnowned();
    if (unowned > 0) {
      if (!(await this.confirmAdoption(unowned))) return;
      await this.options.store.adopt(account);
    }

    await this.run();
  }

  private async run(): Promise<void> {
    this.busy = true;
    this.button.disabled = true;
    this.label.textContent = 'Sharing…';

    // Put into the page before it is given anything to say, so that the announcement is a
    // change to a region already there rather than the arrival of one.
    this.report.hidden = false;
    this.say('Connecting to your other sessions…');
    this.progress.hidden = false;
    this.progress.removeAttribute('value');

    try {
      const result = await this.options.sharing.share((progress) => {
        this.showProgress(progress);
      });
      // The status line carries progress, which is over; the outcome goes where the
      // failures go, so that both ends of a share are reported in the same place.
      this.say('');
      this.options.onNotice(describe(result));
    } catch (error) {
      this.say('');
      this.explain(error);
    } finally {
      this.busy = false;
      this.progress.hidden = true;
      this.report.hidden = true;
      this.render();
    }
  }

  private showProgress(progress: Progress): void {
    if (progress.totalBytes === 0) {
      this.say('Comparing libraries…');
      this.progress.removeAttribute('value');
      return;
    }

    this.progress.max = progress.totalBytes;
    this.progress.value = progress.movedBytes;
    this.say(
      `${formatBytes(progress.movedBytes)} of ${formatBytes(progress.totalBytes)}`,
    );
  }

  /**
   * Report where the share has got to, in the line and on the block that holds it.
   *
   * Both, because folding the sidebar leaves the bar and takes the line: the title is what
   * a pointer resting on the bar then finds, and is the only place the figures survive.
   */
  private say(text: string): void {
    this.status.textContent = text;
    this.report.title = text;
  }

  /**
   * Say what went wrong, in terms of what to do about it.
   *
   * The detail matters most for a failure to connect, because the explanation is not
   * obvious and the remedy — be on the same network — is not something anybody would guess
   * from "sharing failed".
   */
  private explain(error: unknown): void {
    if (error instanceof NoOtherSessionsError) {
      this.options.onNotice({
        level: 'warning',
        message: 'No other sessions are open, so there was nobody to share with.',
      });
      return;
    }

    if (error instanceof NoRouteError) {
      this.options.onNotice({
        level: 'error',
        title: 'Could not reach your other sessions',
        message:
          'Your sessions are signed in, but no direct connection between them could be ' +
          'opened. Models are sent straight from one browser to the other and never ' +
          'through the server, so this needs a network route between the two.',
        detail:
          'This usually means the two are on different networks. Sharing between devices ' +
          'on the same home or office network normally works; between a home network and ' +
          'a mobile connection it often does not.',
      });
      return;
    }

    log.error('A share did not finish.', error);
    this.options.onNotice({
      level: 'error',
      title: 'Sharing did not finish',
      message: 'Something went wrong while copying models between your sessions.',
      detail:
        'Nothing has been lost — each session still has everything it had before. Trying ' +
        'again is safe.',
    });
  }

  /** Ask before attaching a library that belongs to nobody to the account signing in. */
  private async confirmAdoption(count: number): Promise<boolean> {
    const models = count === 1 ? '1 model' : `${count.toString()} models`;

    this.show(
      'Add your models to this account?',
      `${models} in this browser were added before you signed in, so they do not belong ` +
        'to any account yet. Sharing will attach them to the account you are signed in as, ' +
        'and copy them to your other sessions.',
      'Do this only on a browser that is yours. It cannot be undone from here, though the ' +
        'models stay in this browser either way.',
      { confirm: 'Add them and share' },
    );

    return new Promise<boolean>((resolve) => {
      // The close event is queued rather than fired inline, so confirming would otherwise
      // answer twice: once for the button and once for the dialog closing behind it.
      let settled = false;
      const finish = (answer: boolean): void => {
        if (settled) return;
        settled = true;
        this.dialogConfirm.removeEventListener('click', onConfirm);
        this.dialog.removeEventListener('close', onClose);
        this.dialogConfirm.hidden = true;
        this.dialogClose.textContent = 'Close';
        resolve(answer);
      };

      const onConfirm = (): void => {
        this.dialog.close();
        finish(true);
      };
      // Covers the Close button, Escape, and any other way out: all of them mean no.
      const onClose = (): void => {
        finish(false);
      };

      this.dialogConfirm.addEventListener('click', onConfirm);
      this.dialog.addEventListener('close', onClose);
    });
  }

  private show(
    title: string,
    message: string,
    detail: string,
    options: { confirm?: string } = {},
  ): void {
    this.dialogTitle.textContent = title;
    this.dialogMessage.textContent = message;
    this.dialogDetail.textContent = detail;

    const confirming = options.confirm !== undefined;
    this.dialogConfirm.hidden = !confirming;
    this.dialogConfirm.textContent = options.confirm ?? 'Continue';
    this.dialogClose.textContent = confirming ? 'Cancel' : 'Close';

    this.dialog.showModal();
  }
}

/**
 * What a finished share amounts to.
 *
 * A share that reached some of the sessions and not others is a warning rather than an
 * outcome to celebrate: it has not finished the job, and reporting only what moved would
 * suggest it had.
 */
function describe(result: ShareResult): NoticeInput {
  const parts: string[] = [];
  if (result.received > 0) parts.push(`received ${result.received.toString()}`);
  if (result.sent > 0) parts.push(`sent ${result.sent.toString()}`);
  if (result.removed > 0) parts.push(`removed ${result.removed.toString()} here`);
  // Without this, sharing a deletion — which is the whole of what some shares do — reports
  // "nothing to copy", which reads as though the deletion had not travelled.
  if (result.removedElsewhere > 0) {
    parts.push(`removed ${result.removedElsewhere.toString()} elsewhere`);
  }
  // And for the same reason. A share whose whole effect was carrying a colour or a size
  // across would otherwise report that there was nothing to do.
  if (result.revised > 0) parts.push(`restyled ${result.revised.toString()} here`);
  if (result.revisedElsewhere > 0) {
    parts.push(`restyled ${result.revisedElsewhere.toString()} elsewhere`);
  }

  const missed =
    result.unreachable === 1 ? '1 session' : `${result.unreachable.toString()} sessions`;

  if (parts.length === 0) {
    return result.unreachable > 0
      ? {
          level: 'warning',
          message: `Already in step with the sessions that could be reached. ${missed} could not be.`,
        }
      : { level: 'info', message: 'Already in step — nothing to copy.' };
  }

  const summary = `Shared: ${parts.join(', ')}.`;

  return result.unreachable === 0
    ? { level: 'info', message: summary }
    : {
        level: 'warning',
        message: `${summary} ${missed} could not be reached.`,
        detail: 'Those sessions still have what they had before. Sharing again is safe.',
      };
}

/** `element` returns an `HTMLElement`; a progress bar needs its own value and max. */
function progressElement(id: string): HTMLProgressElement {
  const found = element(id);
  if (!(found instanceof HTMLProgressElement)) {
    throw new TypeError(`#${id} is not a progress element`);
  }
  return found;
}
